import React from 'react';
import { Description, HeaderContent, HeaderLeftSection, Icon, SearchBar, Title, TitleSection } from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { isEmpty } from '../../../helpers/common';
import IconButton from '../../button/iconButton';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';

/**
 * Modal header component props
 * @typedef {Object} ModalHeaderProps
 * @property {string} [title] - The main title text displayed in the header
 * @property {string} [description] - Optional description text shown below the title
 * @property {() => void} [onClose] - Callback function triggered when the close button is clicked
 * @property {boolean} [removeBottomBorder] - Whether to remove the bottom border of the header
 * @property {string} [iconName] - Name of the icon to display in the header
 * @property {string} [iconViewBox] - ViewBox for the icon SVG
 * @property {string} [iconFill] - Fill color for the icon
 * @property {string} [searchValue] - Current value of the search input
 * @property {(value: string) => void} [onSearchChange] - Callback when search input value changes
 * @property {string} [placeHolder] - Placeholder text for the search input
 * @property {boolean} [autoFocus] - Whether to autofocus the search input
 */
interface ModalHeaderProps {
  title?: string;
  description?: string;
  onClose?: () => void;
  removeBottomBorder?: boolean;
  // icons props
  iconName?: string;
  iconViewBox?: string;
  iconFill?: string;
  // search bar props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  placeHolder?: string;
  autoFocus?: boolean;
}

const ModalHeader = ({
  title,
  description,
  iconName,
  iconViewBox,
  iconFill,
  onClose,
  removeBottomBorder,
  searchValue,
  onSearchChange,
  placeHolder,
  autoFocus
}: ModalHeaderProps) => {
  // other variables
  // isSearchBar is true if searchValue or onSearchChange or placeHolder is present
  const isSearchBar = Boolean(searchValue || onSearchChange || placeHolder);
  // Apply padding if search bar is present or if only one of title/description exists (XOR condition)
  const applyBlockPadding = isSearchBar || !isEmpty(title) !== !isEmpty(description);

  return (
    <>
      <HeaderContent isSearchBar={isSearchBar} removeBottomBorder={removeBottomBorder}>
        <HeaderLeftSection applyBlockPadding={applyBlockPadding}>
          {!isEmpty(iconName) && (
            <Icon>
              <SVGIcon
                name={iconName}
                width={ICON_SIZE.XL}
                height={ICON_SIZE.XL}
                viewBox={iconViewBox}
                fill={iconFill}
              />
            </Icon>
          )}
          {isSearchBar ? (
            <SearchBar
              placeholder={placeHolder}
              onChange={(e) => onSearchChange?.(e.target.value)}
              value={searchValue}
              autoFocus={autoFocus}
              aria-label={placeHolder || 'Search'}
            />
          ) : (
            <TitleSection>
              {!isEmpty(title) && <Title id='modal-title'>{title}</Title>}
              {!isEmpty(description) && <Description>{description}</Description>}
            </TitleSection>
          )}
        </HeaderLeftSection>
        {onClose && (
          <IconButton
            variant={ICON_BUTTON_TYPE.TERTIARY}
            size={ICON_BUTTON_SIZE.SMALL}
            iconName='new-close-icon'
            onClick={onClose}
          />
        )}
      </HeaderContent>
    </>
  );
};

export default ModalHeader;
